import { Transaction, Budget, SavingsGoal } from '../types';

/**
 * Service de sécurité pour la validation et la sanitisation des données
 */
export class SecurityService {
  
  // Validation des caractères dangereux
  private static sanitizeString(input: string): string {
    return input
      .trim()
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Supprime scripts
      .replace(/javascript:/gi, '') // Supprime javascript:
      .replace(/on\w+\s*=/gi, '') // Supprime événements onclick, etc.
      .substring(0, 1000); // Limite la taille
  }

  // Validation d'une transaction
  static validateTransaction(transaction: Omit<Transaction, 'id'>): {
    isValid: boolean;
    errors: string[];
    sanitized?: Omit<Transaction, 'id'>;
  } {
    const errors: string[] = [];
    
    // Validation du type
    if (!transaction.type || !['income', 'expense'].includes(transaction.type)) {
      errors.push('Type de transaction invalide');
    }

    // Validation de la catégorie
    if (!transaction.category || typeof transaction.category !== 'string') {
      errors.push('Catégorie requise');
    } else if (transaction.category.length > 100) {
      errors.push('Catégorie trop longue (max 100 caractères)');
    }

    // Validation du montant
    if (!transaction.amount || typeof transaction.amount !== 'number') {
      errors.push('Montant requis et doit être un nombre');
    } else if (transaction.amount <= 0) {
      errors.push('Le montant doit être positif');
    } else if (transaction.amount > 1000000) {
      errors.push('Montant trop élevé (max 1M)');
    }

    // Validation de la description
    if (!transaction.description || typeof transaction.description !== 'string') {
      errors.push('Description requise');
    } else if (transaction.description.length > 500) {
      errors.push('Description trop longue (max 500 caractères)');
    }

    // Validation de la date
    if (!transaction.date || !(transaction.date instanceof Date)) {
      errors.push('Date invalide');
    } else {
      const now = new Date();
      const futureLimit = new Date();
      futureLimit.setFullYear(now.getFullYear() + 1);
      
      if (transaction.date > futureLimit) {
        errors.push('Date trop dans le futur');
      }
    }

    if (errors.length > 0) {
      return { isValid: false, errors };
    }

    // Sanitisation des données
    const sanitized: Omit<Transaction, 'id'> = {
      type: transaction.type,
      category: this.sanitizeString(transaction.category),
      amount: Math.round(transaction.amount * 100) / 100, // Arrondir à 2 décimales
      description: this.sanitizeString(transaction.description),
      date: transaction.date,
      recurring: Boolean(transaction.recurring),
      recurringPeriod: transaction.recurring ? transaction.recurringPeriod : undefined
    };

    return { isValid: true, errors: [], sanitized };
  }

  // Validation d'un budget
  static validateBudget(budget: Omit<Budget, 'id' | 'spent' | 'remaining'>): {
    isValid: boolean;
    errors: string[];
    sanitized?: Omit<Budget, 'id' | 'spent' | 'remaining'>;
  } {
    const errors: string[] = [];

    // Validation de la catégorie
    if (!budget.category || typeof budget.category !== 'string') {
      errors.push('Catégorie requise');
    } else if (budget.category.length > 100) {
      errors.push('Catégorie trop longue');
    }

    // Validation du montant alloué
    if (!budget.allocated || typeof budget.allocated !== 'number') {
      errors.push('Montant alloué requis');
    } else if (budget.allocated <= 0) {
      errors.push('Le montant alloué doit être positif');
    } else if (budget.allocated > 1000000) {
      errors.push('Montant alloué trop élevé');
    }

    // Validation de la période
    if (!budget.period || !['monthly', 'yearly'].includes(budget.period)) {
      errors.push('Période invalide');
    }

    if (errors.length > 0) {
      return { isValid: false, errors };
    }

    const sanitized: Omit<Budget, 'id' | 'spent' | 'remaining'> = {
      category: this.sanitizeString(budget.category),
      allocated: Math.round(budget.allocated * 100) / 100,
      period: budget.period
    };

    return { isValid: true, errors: [], sanitized };
  }

  // Validation d'un objectif d'épargne
  static validateSavingsGoal(goal: Omit<SavingsGoal, 'id' | 'currentAmount'>): {
    isValid: boolean;
    errors: string[];
    sanitized?: Omit<SavingsGoal, 'id' | 'currentAmount'>;
  } {
    const errors: string[] = [];

    // Validation du nom
    if (!goal.name || typeof goal.name !== 'string') {
      errors.push('Nom requis');
    } else if (goal.name.length > 100) {
      errors.push('Nom trop long');
    }

    // Validation du montant cible
    if (!goal.targetAmount || typeof goal.targetAmount !== 'number') {
      errors.push('Montant cible requis');
    } else if (goal.targetAmount <= 0) {
      errors.push('Le montant cible doit être positif');
    } else if (goal.targetAmount > 10000000) {
      errors.push('Montant cible trop élevé');
    }

    // Validation de la date cible
    if (!goal.targetDate || !(goal.targetDate instanceof Date)) {
      errors.push('Date cible invalide');
    } else {
      const now = new Date();
      const futureLimit = new Date();
      futureLimit.setFullYear(now.getFullYear() + 50);
      
      if (goal.targetDate <= now) {
        errors.push('La date cible doit être dans le futur');
      } else if (goal.targetDate > futureLimit) {
        errors.push('Date cible trop lointaine');
      }
    }

    // Validation de la catégorie
    if (!goal.category || !['general', 'emergency', 'vacation', 'purchase'].includes(goal.category)) {
      errors.push('Catégorie invalide');
    }

    // Validation de la couleur
    if (!goal.color || typeof goal.color !== 'string') {
      errors.push('Couleur requise');
    } else if (!/^#[0-9A-F]{6}$/i.test(goal.color)) {
      errors.push('Format de couleur invalide (utilisez #RRGGBB)');
    }

    if (errors.length > 0) {
      return { isValid: false, errors };
    }

    const sanitized: Omit<SavingsGoal, 'id' | 'currentAmount'> = {
      name: this.sanitizeString(goal.name),
      targetAmount: Math.round(goal.targetAmount * 100) / 100,
      targetDate: goal.targetDate,
      category: goal.category,
      color: goal.color,
      description: goal.description ? this.sanitizeString(goal.description) : undefined
    };

    return { isValid: true, errors: [], sanitized };
  }

  // Validation générale des données utilisateur
  static validateUserData(data: {
    transactions: Transaction[];
    budgets: Budget[];
    savingsGoals: SavingsGoal[];
  }): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Limites de sécurité
    if (data.transactions.length > 10000) {
      errors.push('Trop de transactions (max 10000)');
    }
    if (data.budgets.length > 100) {
      errors.push('Trop de budgets (max 100)');
    }
    if (data.savingsGoals.length > 100) {
      errors.push('Trop d\'objectifs d\'épargne (max 100)');
    }

    return { isValid: errors.length === 0, errors };
  }

  // Chiffrement simple pour les données sensibles (optionnel)
  static encryptSensitiveData(data: string, key: string): string {
    // Simple XOR encryption pour les données non critiques
    let encrypted = '';
    for (let i = 0; i < data.length; i++) {
      encrypted += String.fromCharCode(
        data.charCodeAt(i) ^ key.charCodeAt(i % key.length)
      );
    }
    return btoa(encrypted);
  }

  static decryptSensitiveData(encryptedData: string, key: string): string {
    try {
      const data = atob(encryptedData);
      let decrypted = '';
      for (let i = 0; i < data.length; i++) {
        decrypted += String.fromCharCode(
          data.charCodeAt(i) ^ key.charCodeAt(i % key.length)
        );
      }
      return decrypted;
    } catch {
      return '';
    }
  }

  // Validation de l'intégrité des données
  static validateDataIntegrity(data: any): boolean {
    try {
      // Vérification que les données sont des objets valides
      if (typeof data !== 'object' || data === null) {
        return false;
      }

      // Vérification JSON valide
      JSON.stringify(data);
      
      return true;
    } catch {
      return false;
    }
  }

  // Détection d'injection de code
  static detectMaliciousCode(input: string): boolean {
    const maliciousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /eval\s*\(/i,
      /function\s*\(/i,
      /setTimeout/i,
      /setInterval/i
    ];

    return maliciousPatterns.some(pattern => pattern.test(input));
  }
}